import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { proxyUrl } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import {
  positionsMap,
  type LineupPosition,
  type SelectedPlayers,
} from '#src/components/HowToPlay/CreateATeam/constants.ts';
import { CardImg } from '#src/components/card/CardImg.tsx';
import { PositionIcon } from '#src/components/lineup/PositionIcon/index.tsx';
import { CaptainToggle } from '#src/components/so5/CaptainToggle/index.tsx';
import { positionShortNames } from '#src/lib/players.ts';

const appearIn = keyframes`
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  50% {
    scale: 1.1;
  }
  100% {
    scale: 1;
  }
`;

const CardImgWrapper = styled(Vertical).attrs({ center: true })`
  width: calc(var(--unit) * 15);
  aspect-ratio: var(--card-aspect-ratio);
  > *:first-child {
    animation: 0.3s ease ${appearIn} forwards;
  }
`;

const SlotPlaceholder = styled(Vertical).attrs({ center: true })`
  width: calc(var(--unit) * 15);
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: var(--half-unit);
  background: var(--c-nd-50);
  border: 1px solid var(--c-nd-200);
  &.selected {
    border: 1px solid var(--c-blue-400);
    background: linear-gradient(to top, var(--c-blue-400) 0%, transparent 60%);
  }
`;

const StyledButtonBase = styled(ButtonBase)`
  width: calc(var(--unit) * 15);
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: var(--unit);
  background: linear-gradient(to top, var(--c-nd-200) 0%, transparent 90%);
`;

const Container = styled.div`
  position: relative;
  width: calc(var(--unit) * 15);
  aspect-ratio: var(--card-aspect-ratio);
`;

const CaptainToggleWrapper = styled.div`
  display: none;
  &.isVisible {
    display: block;
    position: absolute;
    left: var(--unit);
    top: 0;
    z-index: 2;
  }
`;

type Props = {
  selectedPlayers: SelectedPlayers;
  setSelectedCaptain: (position: LineupPosition) => void;
  selectedCaptain: string | null;
  currentPosition: LineupPosition | null;
  currentPositionData: any;
};

export const Lineup = ({
  selectedPlayers,
  setSelectedCaptain,
  selectedCaptain,
  currentPosition,
  currentPositionData,
}: Props) => {
  const handleSelectCaptain = (position: LineupPosition) => {
    setSelectedCaptain(position);
  };
  return (
    <Horizontal center>
      {positionsMap.map((_, index) => {
        const position = positionsMap[index];
        return (
          <Container key={position.position}>
            <CaptainToggleWrapper
              className={classnames({
                isVisible: currentPositionData?.position === undefined,
              })}
            >
              <CaptainToggle active={selectedCaptain === position.position} />
            </CaptainToggleWrapper>
            <CardImgWrapper>
              {selectedPlayers[position.position] ? (
                <StyledButtonBase
                  onClick={() => handleSelectCaptain(position.position)}
                  style={{
                    mask: `url(${proxyUrl(selectedPlayers[position.position] || '', { cropWidth: 160 })}) center / cover no-repeat`,
                  }}
                >
                  <CardImg
                    src={selectedPlayers[position.position]}
                    width={160}
                  />
                </StyledButtonBase>
              ) : (
                <SlotPlaceholder
                  className={classnames({
                    selected: position.position === currentPosition,
                  })}
                >
                  <PositionIcon position={positionsMap[index].position} />
                  <LabelM bold>
                    <FormattedMessage
                      {...(position.label
                        ? position.label
                        : positionShortNames[position.position])}
                    />
                  </LabelM>
                </SlotPlaceholder>
              )}
            </CardImgWrapper>
          </Container>
        );
      })}
    </Horizontal>
  );
};
